// src/pages/Admin/Dashboard/Events/components/EventForm.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaImage,
  FaSave,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  buildInitialFormFromEvent,
  getMediaUrl,
  normalizeEventPayload,
  validateEventForm,
} from "../utils/eventsUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

function TextInput({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );
}

function TextArea({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  rows = 4,
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>

      <textarea
        name={name}
        value={value ?? ""}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className={`${inputClass} resize-none`}
      />
    </div>
  );
}

function ToggleInput({ label, helper, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
      />

      <span>
        <span className="block text-sm font-bold text-slate-800 dark:text-slate-100">
          {label}
        </span>
        {helper && (
          <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
            {helper}
          </span>
        )}
      </span>
    </label>
  );
}

export default function EventForm({
  mode = "create",
  event,
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(() => buildInitialFormFromEvent(event), [event]);

  const [formData, setFormData] = useState(initialForm);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    setFormData(initialForm);
    setPreviewUrl(getMediaUrl(initialForm.photo_url));
  }, [initialForm]);

  useEffect(() => {
    if (!formData.imageFile) return undefined;

    const objectUrl = URL.createObjectURL(formData.imageFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.imageFile]);

  const handleChange = (changeEvent) => {
    const { name, value, files, type, checked } = changeEvent.target;

    if (name === "imageFile") {
      setFormData((prev) => ({
        ...prev,
        imageFile: files?.[0] || null,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "photo_url") {
      setPreviewUrl(getMediaUrl(value));
    }
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();

    const payload = normalizeEventPayload(formData);
    const errorMessage = validateEventForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit({
      ...payload,
      imageFile: formData.imageFile,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="flex items-center gap-2 text-base font-bold text-slate-950 dark:text-white">
          <FaCalendarAlt className="text-blue-500" />
          Informations principales
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <TextInput
              label="Titre de l'événement"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              required
              placeholder="Ex : Stage de judo vacances, Passage de grades..."
            />
          </div>

          <TextInput
            label="Type d'événement"
            name="type_event"
            value={formData.type_event}
            onChange={handleChange}
            placeholder="Ex : Stage, Compétition, Animation..."
          />

          <TextInput
            label="Catégorie"
            name="cate_event"
            value={formData.cate_event}
            onChange={handleChange}
            placeholder="Ex : Mini-poussins, Benjamins, Tous publics..."
          />

          <TextInput
            label="Niveau"
            name="level_event"
            value={formData.level_event}
            onChange={handleChange}
            placeholder="Ex : Débutants, Confirmés, Tous niveaux..."
          />

          <TextInput
            label="Lieu"
            name="lieu_event"
            value={formData.lieu_event}
            onChange={handleChange}
            placeholder="Ex : Dojo municipal, Gymnase..."
          />

          <TextInput
            label="Date de début"
            name="start"
            value={formData.start}
            onChange={handleChange}
            type="date"
          />

          <TextInput
            label="Date de fin"
            name="end"
            value={formData.end}
            onChange={handleChange}
            type="date"
          />

          <div className="sm:col-span-2">
            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Présentez l'événement, le programme, les conditions..."
              rows={5}
            />
          </div>

          <div className="sm:col-span-2">
            <TextArea
              label="Horaires / programme de la journée"
              name="horaire_event"
              value={formData.horaire_event}
              onChange={handleChange}
              placeholder={`Ex :
Pesée : 8h30 - 9h00
Début des combats : 9h30
Remise des récompenses : 16h00`}
              rows={5}
            />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Image de présentation
        </h3>

        <div className="mt-4 grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Aperçu événement"
                className="h-52 w-full object-cover"
              />
            ) : (
              <div className="flex h-52 flex-col items-center justify-center text-slate-400">
                <FaImage className="text-4xl" />
                <p className="mt-3 text-sm font-medium">Aucune image</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Télécharger une image</label>

              <label className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center transition hover:border-blue-300 hover:bg-blue-50/50 dark:border-slate-700 dark:bg-slate-950 dark:hover:border-blue-500/40 dark:hover:bg-blue-500/10">
                <FaUpload className="text-xl text-blue-500" />
                <span className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Choisir un fichier
                </span>
                <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  JPG, PNG ou WEBP recommandé
                </span>

                <input
                  type="file"
                  name="imageFile"
                  accept="image/*"
                  onChange={handleChange}
                  className="sr-only"
                />
              </label>
            </div>

            <TextInput
              label="Ou URL / chemin de l'image"
              name="photo_url"
              value={formData.photo_url}
              onChange={handleChange}
              placeholder="/uploads/events/image.jpg ou https://..."
            />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Inscriptions et publication
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Lien agenda externe"
            name="agenda_url"
            value={formData.agenda_url}
            onChange={handleChange}
            placeholder="https://..."
          />

          <TextInput
            label="Nombre maximum d'inscriptions"
            name="max_registrations"
            value={formData.max_registrations}
            onChange={handleChange}
            type="number"
            placeholder="Vide = illimité"
          />

          <ToggleInput
            label="Événement actif"
            helper="Si activé, l'événement pourra être affiché côté visiteur."
            checked={Boolean(formData.is_active)}
            onChange={(toggleEvent) =>
              setFormData((prev) => ({
                ...prev,
                is_active: toggleEvent.target.checked,
              }))
            }
          />

          <ToggleInput
            label="Inscriptions ouvertes"
            helper="Si activé, les visiteurs pourront s'inscrire à cet événement."
            checked={Boolean(formData.can_register)}
            onChange={(toggleEvent) =>
              setFormData((prev) => ({
                ...prev,
                can_register: toggleEvent.target.checked,
              }))
            }
          />
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : mode === "create"
                ? "Créer l'événement"
                : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}
